<template>
  <div class="content">
    <div class="row">
      <div class="col-8 ignore-screen">
        <h2 class="font-light">New Exam</h2>
      </div>
      <div class="col-4 ignore-screen u-vertical-center u-inline-flex">
        <div class="form-group u-fill-width u-inline u-text-right">
          <a @click="$router.go(-1)">
            <button class="btn-info u-pull-right">
              <i
                class="fa-wrapper fa fa-reply normal-icon pad-right"
                aria-hidden="true"
              ></i>
              Back
            </button>
          </a>
        </div>
      </div>
    </div>
    <server-error v-bind:error="error.serverError"></server-error>
    <div class="form-section">
      <label class="font-bold">Candidate</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-model="form.candidate"
            :options="allOtherUsers"
            :multiple="true"
            :close-on-select="false"
            label="name"
            :custom-label="getLabel"
            :show-labels="false"
            track-by="id"
            v-bind:class="{ 'input-error': error.candidate }"
          ></multiselect>
          <span class="error">{{ error.candidate }}</span>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label class="font-bold">Exam Title</label>
      <span v-if="error.title" class="error">{{ error.title }}</span>
      <div class="section-body row u-no-padding">
        <div class="col-5">
          <input
            v-model="form.title"
            placeholder="Enter a title..."
            v-bind:class="{ 'input-error': error.title }"
          />
        </div>
        <div class="font-light col-2 u-text-right u-vertical-center">
          Standard Exams:
        </div>
        <div class="col-5">
          <multiselect
            :options="examTemplates"
            label="title"
            v-model="selectedExam"
          ></multiselect>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label class="font-bold">Topics</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-model="form.topics"
            :options="topics"
            :multiple="true"
            label="name"
            track-by="id"
            :close-on-select="true"
            v-bind:class="{ 'input-error': error.topics }"
          ></multiselect>
          <span class="error">{{ error.topics }}</span>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label class="font-bold">Supervisor</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-model="form.supervisor"
            :options="users"
            label="name"
            :custom-label="getLabel"
            :show-labels="false"
            track-by="id"
            :close-on-select="true"
            v-bind:class="{ 'input-error': error.supervisor }"
          ></multiselect>
          <span class="error">{{ error.supervisor }}</span>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label class="font-bold">Exam Details</label>
      <div class="section-body row u-no-padding">
        <div class="col-4">
          <label class="font-light">Pass Mark (%)</label>
          <input
            v-model="form.pass_percentage"
            type="number"
            min="0"
            max="100"
            placeholder="Percentage"
            v-bind:class="{ 'input-error': error.pass_percentage }"
            v-bind:disabled="!permissions.includes('exams.change_exam')"
          />
          <span class="error">{{ error.pass_percentage }}</span>
        </div>
        <div class="col-4">
          <label class="font-light">Number of Questions</label>
          <input
            v-model="form.num_questions"
            type="number"
            min="0"
            max="200"
            placeholder="How may questions?"
            v-bind:class="{ 'input-error': error.num_questions }"
            v-bind:disabled="!permissions.includes('exams.change_exam')"
          />
          <span class="error">{{ error.num_questions }}</span>
        </div>
        <div class="col-4">
          <label class="font-light">Min Questions Per Topic</label>
          <input
            v-model="form.min_per_topic"
            type="number"
            min="0"
            max="100"
            placeholder="Min. number questions/topic"
            v-bind:class="{ 'input-error': error.min_per_topic }"
            v-bind:disabled="!permissions.includes('exams.change_exam')"
          />
          <span class="error">{{ error.min_per_topic }}</span>
        </div>
        <space class="large" v-if="!permissions.includes('exams.change_exam')">
        </space>
        <div class="u-box" v-if="!permissions.includes('exams.change_exam')">
          <h5 class="font-light">Custom Exam Details</h5>
          The <i>Exam Details</i> are configured automatically when you select
          one of the <i>Standard Exams</i>. Please see one of the admin or
          management team if you need a new exam setup.
        </div>
      </div>
      <div class="panel">
        <button class="btn-primary" v-on:click="save">
          <loading-spinner v-show="saving"></loading-spinner>
          <i class="fa-wrapper fa fa-upload pad-right" v-show="!saving"></i>
          Save
        </button>
      </div>
    </div>
  </div>
</template>
<style>
.form-section {
  width: 100%;
}
</style>
<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { debounce } from "@/functions/debounce.function";
import Multiselect from "vue-multiselect";
import ServerError from "@/components/ServerError";
import LoadingSpinner from "@/components/LoadingSpinner";
import { getPilotLabel } from "@/functions/getPilotLabel";
import { clearFormFieldErrors } from "@/functions/clearFormFieldErrors";

export default {
  name: "exam-create",
  components: {
    Multiselect,
    ServerError,
    LoadingSpinner
  },
  data: function() {
    return {
      form: {},
      selectedExam: {},
      saving: false
    };
  },
  computed: {
    ...mapGetters("auth", ["users", "userProfile", "permissions"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("exams", ["examTemplates"]),
    ...mapGetters("questions", ["topics"]),
    allOtherUsers: function() {
      return this.users.filter(u => u.id != this.userProfile.id);
    }
  },
  watch: {
    form: {
      // Handler to clear the error on a field when it's value is changed
      handler: debounce(function(formFields) {
        clearFormFieldErrors(formFields);
        this.selectedExam = {};
      }, 300),
      deep: true
    },
    selectedExam: function(val) {
      for (let field in val) {
        if (field != "topics") {
          this.form[field] = val[field];
        } else {
          // Field == topics
          var stack = [];
          for (let idx of val[field]) {
            const t = this.topics.find(obj => obj.id == idx);
            stack.push(t);
          }
          this.form.topics = stack;
        }
      }
    }
  },
  methods: {
    getLabel(payload) {
      return getPilotLabel(payload);
    },
    validate() {
      // Required fields
      const required = [
        "candidate",
        "title",
        "topics",
        "supervisor",
        "pass_percentage",
        "num_questions",
        "min_per_topic"
      ];
      var isValid = true;
      for (let field of required) {
        if (this.form[field] == undefined) {
          let err = {};
          err[field] = "This field is required.";
          this.$store.commit("core/setError", err);
          isValid = false;
        }
      }
      return isValid;
    },
    save() {
      var isValid = this.validate();
      // Exit if the form isn't valid
      if (!isValid) {
        return;
      }
      // Submit stuff to the server =)
      this.saving = true;
      var cleanedData = Object.assign({}, this.form);
      // Clean the supervisor field
      cleanedData.supervisor = this.form.supervisor.id;
      // Clean the topics field
      var topics = [];
      for (let idx in this.form.topics) {
        topics.push(this.form.topics[idx].id);
      }
      cleanedData.topics = topics;
      // Work out where to redirect to next
      if (this.form.candidate.length == 1) {
        cleanedData.redirect = "exam-detail";
      } else {
        cleanedData.redirect = "exam-list";
      };
      for (let candidate of this.form.candidate) {
        // Clean the candidate field
        cleanedData.candidate = candidate.id;
        this.$store
          .dispatch("exams/createExam", cleanedData)
          .then(() => (this.saving = false));
      }
    }
  },
  mounted() {
    this.$store.commit("core/clearErrors");
    this.$store.commit(
      "questions/setTopics",
      this.$offlineStorage.get("topics")
    );
    this.$store.commit("auth/setUsers", this.$offlineStorage.get("users"));
    this.$store.commit(
      "exams/setExamTemplates",
      this.$offlineStorage.get("examTemplates")
    );
    // Handle pre-selected candidates passed through an URL query
    if (Object.keys(this.$route.query).includes("candidate")) {
      let candidate = [this.users.find(u => u.id == this.$route.query.candidate)];
      Vue.set(this.form, "candidate", candidate);
      this.$router.replace({ name: "exam-create" });
    };
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
